import { FEATURES, isFeatureEnabledForOrg } from 'src/config/features'
import { MODULES } from 'src/config/modules'

function keepDefaultView(to, from) {
  if (from.matched.length) {
    to.matched.at(-1).components.default = from.matched.at(-1).components.default
  }
}

function addStateInMeta(to, from) {
  if (from.matched.length) {
    to.meta = {
      ...to.meta,
      params: from.params,
      query: from.query
    }
  }
}

// Routes with `meta.requiresFeature` are only reachable when that org feature is enabled;
// otherwise redirect to `meta.featureFallback` (same params), covering bookmarks and old links.
function requireOrgFeature(to) {
  const { requiresFeature, featureFallback } = to.meta
  if (!requiresFeature || isFeatureEnabledForOrg(requiresFeature)) return true
  return { name: featureFallback, params: to.params, query: to.query, replace: true }
}

// Time Studies improvements phase (Outline → Kaizen Notes); falls back to the Time Study result.
const sdImprovementsMeta = {
  requiresFeature: FEATURES.SD_IMPROVEMENTS,
  featureFallback: 'timeStudy'
}

export default [
  {
    path: '/user',
    name: 'User Layout',
    component: () => import('src/components/Core/AppLayout.vue'),
    children: [
      {
        path: 'home',
        name: 'Home',
        component: () => import('src/components/User/Home/Index.vue')
      },
      {
        path: 'file-manager',
        name: 'File Manager',
        component: () => import('src/components/User/FileManager/Index.vue'),
        children: [
          {
            path: ':id?',
            name: 'Files Manager Home',
            component: () => import('src/components/User/FileManager/Home.vue'),
            meta: { title: 'Files Manager Home', parent: 'File Manager' }
          },
          {
            path: 'edit/:fileId',
            name: 'Edit Video File',
            component: () =>
              import('src/components/User/FileManager/VideoEditing/VideoEditing.vue'),
            meta: { title: 'Video Editing', parent: 'File Manager' },
            beforeEnter: [addStateInMeta]
          },
          {
            path: 'search',
            name: 'Search File Manager',
            component: () => import('src/components/User/FileManager/Search.vue'),
            meta: { title: 'Search Result', parent: 'File Manager' },
            beforeEnter: [addStateInMeta]
          }
        ]
      },
      {
        path: 'org-users',
        name: 'Users',
        component: () => import('src/components/User/Users/Index.vue')
      },
      {
        path: 'user-manual',
        name: 'User Manual',
        component: () => import('src/components/User/UserManual/Index.vue')
      },
      {
        path: 'app-fundamental',
        name: 'App Fundamental',
        component: () => import('src/components/User/AppFundamental/Index.vue')
      },
      {
        path: 'time-studies',
        name: 'Time Studies',
        meta: { moduleCode: MODULES.STATION_DESIGN.moduleCode },
        component: () => import('src/components/User/StationBalancing/Index.vue'),
        children: [
          {
            path: 'studies/:id(\\d+)?',
            name: 'studies',
            component: () => import('src/components/User/StationBalancing/StudyListv2.vue'),
            meta: { title: 'Time Studies', parent: 'Time Studies' }
          },
          {
            path: 'search',
            name: 'Search Staion Design Studies',
            component: () =>
              import('src/components/User/StationBalancing/FolderManagement/Search.vue'),
            meta: { title: 'Search Result', parent: 'Time Studies' },
            beforeEnter: [addStateInMeta]
          },
          {
            path: 'comparison/:id',
            name: 'sd-comparison',
            component: () =>
              import('src/components/User/StationBalancing/AssessmentComparison/Index.vue'),
            meta: { title: 'Time Studies Comparison', parent: 'Time Studies' }
          },
          {
            path: 'study/:id',
            name: 'new-study',
            component: () => import('src/components/User/StationBalancing/NewStudy.vue'),
            children: [
              {
                path: 'cycles',
                name: 'cycles',
                component: () =>
                  import('src/components/User/StationBalancing/components/DefineCycles.vue'),
                meta: {
                  index: 0,
                  icon: 'ArrowRightOutlined',
                  next: 'divide'
                }
              },
              {
                path: 'divide',
                name: 'divide',
                component: () =>
                  import('src/components/User/StationBalancing/components/DivideSegments.vue'),
                meta: {
                  index: 1,
                  icon: 'ArrowRightOutlined',
                  previous: 'cycles',
                  next: 'steps'
                }
              },
              {
                path: 'steps',
                name: 'steps',
                component: () =>
                  import('src/components/User/StationBalancing/components/DefineSteps.vue'),
                meta: {
                  index: 2,
                  icon: 'ArrowRightOutlined',
                  previous: 'divide',
                  next: 'timeStudy'
                }
              },
              {
                path: 'time-study',
                name: 'timeStudy',
                components: {
                  default: () =>
                    import(
                      'src/components/User/StationBalancing/components/TimeMotionStudyResult.vue'
                    ),
                  child: () =>
                    import('src/components/User/StationBalancing/components/CompareTimeStudy.vue')
                },
                meta: {
                  index: 3,
                  icon: 'ArrowRightOutlined',
                  previous: 'steps',
                  next: 'outline'
                }
              },
              {
                path: 'outline',
                name: 'outline',
                component: () =>
                  import('src/components/User/StationBalancing/components/MultipleRegions.vue'),
                meta: {
                  index: 4,
                  icon: 'ArrowRightOutlined',
                  previous: 'timeStudy',
                  next: 'improvements',
                  ...sdImprovementsMeta
                },
                beforeEnter: [requireOrgFeature]
              },
              {
                path: 'improvements',
                name: 'improvements',
                component: () =>
                  import(
                    'src/components/User/StationBalancing/components/ImprovementQuestions.vue'
                  ),
                meta: {
                  index: 5,
                  icon: 'ArrowRightOutlined',
                  previous: 'outline',
                  next: 'audit',
                  ...sdImprovementsMeta
                },
                beforeEnter: [requireOrgFeature]
              },
              {
                path: 'audit',
                name: 'audit',
                component: () =>
                  import('src/components/User/StationBalancing/components/WorkstationAudit.vue'),
                meta: {
                  index: 6,
                  icon: 'ArrowRightOutlined',
                  previous: 'improvements',
                  next: 'suggestions',
                  ...sdImprovementsMeta
                },
                beforeEnter: [requireOrgFeature]
              },
              {
                path: 'suggestions',
                name: 'suggestions',
                component: () =>
                  import('src/components/User/StationBalancing/components/ViewResult.vue'),
                meta: {
                  index: 7,
                  icon: 'ArrowRightOutlined',
                  previous: 'audit',
                  next: 'notes',
                  ...sdImprovementsMeta
                },
                beforeEnter: [requireOrgFeature]
              },
              {
                path: 'notes',
                name: 'notes',
                component: () =>
                  import('src/components/User/StationBalancing/components/KaizenNotes.vue'),
                meta: {
                  index: 8,
                  icon: 'ArrowRightOutlined',
                  previous: 'suggestions',
                  ...sdImprovementsMeta
                },
                beforeEnter: [requireOrgFeature]
              }
            ]
          }
        ]
      },
      {
        // Legacy path: /user/station-design/... -> /user/time-studies/...
        // Keeps existing bookmarks and shared links working after the rename.
        path: 'station-design/:pathMatch(.*)*',
        redirect: (to) => ({
          path: `/user/time-studies/${
            Array.isArray(to.params.pathMatch) ? to.params.pathMatch.join('/') : to.params.pathMatch || ''
          }`,
          query: to.query
        })
      },
      {
        path: 'floor-layout',
        name: 'Floor Layout',
        meta: { moduleCode: MODULES.FLOOR_ANALYSIS.moduleCode },
        component: () => import('src/components/User/CellOptimization/Index.vue'),
        children: [
          // {
          //   path: 'studies',
          //   name: 'cellStudies',
          //   component: () => import('src/components/User/CellOptimization/CellStudyList.vue'),
          //   meta: { title: 'Cell Studies', parent: 'Floor Layout' }
          // },
          {
            path: 'studies/:id(\\d+)?',
            name: 'cellStudies',
            component: () => import('src/components/User/CellOptimization/CellStudyListv2.vue'),
            meta: { title: 'Cell Studies', parent: 'Floor Layout' }
          },
          {
            path: 'search',
            name: 'Search Floor analysis Records',
            component: () =>
              import('src/components/User/CellOptimization/FolderManagement/Search.vue'),
            meta: { title: 'Cell Studies', parent: 'Floor Layout' },
            beforeEnter: [addStateInMeta]
          },
          {
            path: 'study/:id',
            name: 'cell-new-study',
            components: {
              default: () => import('src/components/User/CellOptimization/CellStudyList.vue'),
              modal: () => import('src/components/User/CellOptimization/CellNewStudy.vue')
            },
            children: [
              {
                path: 'video',
                name: 'cell-video',
                components: {
                  modal: () =>
                    import('src/components/User/CellOptimization/components/CellVideo.vue')
                },
                meta: {
                  index: 0,
                  text: 'Done with Video Trimming',
                  icon: 'ArrowRightOutlined',
                  next: 'cell-outline'
                }
              },
              {
                path: 'outline',
                name: 'cell-outline',
                component: () =>
                  import('src/components/User/CellOptimization/components/Outline.vue'),
                meta: {
                  index: 1,
                  text: 'Region Marking and Dimension',
                  icon: 'ArrowRightOutlined',
                  previous: 'cell-video',
                  next: 'merge-tracklets'
                }
              },
              {
                path: 'merge',
                name: 'merge-tracklets',
                component: () =>
                  import('src/components/User/CellOptimization/components/MergeTracklets.vue'),
                meta: {
                  index: 2,
                  text: 'Region Marking and Dimension',
                  icon: 'ArrowRightOutlined',
                  previous: 'cell-outline',
                  next: 'cell-results'
                }
              },
              {
                path: 'results',
                name: 'cell-results',
                component: () =>
                  import('src/components/User/CellOptimization/components/Overview.vue'),
                meta: {
                  index: 3,
                  text: 'Done with Overview',
                  icon: 'ArrowRightOutlined',
                  previous: 'merge-tracklets'
                }
              }
            ]
          }
        ]
      },

      {
        path: 'product-information',
        name: 'Product Information',
        component: () => import('src/components/User/ProductInformation/Index.vue')
      },
      {
        path: 'line-balancing',
        name: 'Line Balancing',
        meta: { moduleCode: MODULES.LINE_BALANCING.moduleCode },
        component: () => import('src/components/User/LineBalancing/Index.vue'),
        children: [
          // {
          //   path: 'lines',
          //   name: 'Lines',
          //   component: () => import('src/components/User/LineBalancing/components/LinesTable.vue'),
          //   meta: { title: 'Line Balancing', parent: 'Line Balancing' }
          // },
          {
            path: 'lines/:id(\\d+)?',
            name: 'Lines',
            component: () =>
              import('src/components/User/LineBalancing/components/LinesTablev2.vue'),
            meta: { title: 'Line Balancing', parent: 'Line Balancing' }
          },
          {
            path: 'search',
            name: 'Search Line Balancing Records',
            component: () =>
              import('src/components/User/LineBalancing/components/FolderManagement/Search.vue'),
            meta: { title: 'Line Balancing', parent: 'Line Balancing' },
            beforeEnter: [addStateInMeta]
          },
          // Design Flow (New Line) - Nested Routes
          {
            path: 'design-flow/:lineStudyId',
            name: 'Design Flow',
            component: () => import('src/components/User/LineBalancing/components/DesignFlow.vue'),
            meta: { parent: 'Line Balancing', title: 'Design New Line' },
            children: [
              {
                path: 'work-content',
                name: 'Design Work Content',
                component: () =>
                  import(
                    'src/components/User/LineBalancing/components/WorkContent/WorkContent.vue'
                  ),
                meta: { parent: 'Line Balancing', title: 'Work Content' }
              }
            ]
          },
          // Rebalance Flow - Nested Routes
          {
            path: 'rebalance-flow/:lineStudyId',
            name: 'Rebalance Flow',
            component: () =>
              import('src/components/User/LineBalancing/components/RebalanceFlow.vue'),
            meta: { parent: 'Line Balancing', title: 'Rebalance Line' },
            children: [
              {
                path: 'line-details',
                name: 'Rebalance Line Details',
                component: () =>
                  import(
                    'src/components/User/LineBalancing/components/LineDetails/LineDetails.vue'
                  ),
                meta: { parent: 'Line Balancing', title: 'Line Details' }
              },
              {
                path: 'work-content',
                name: 'Rebalance Work Content',
                component: () =>
                  import(
                    'src/components/User/LineBalancing/components/WorkContent/WorkContent.vue'
                  ),
                meta: { parent: 'Line Balancing', title: 'Work Content' }
              }
            ]
          },
          // Legacy routes for backward compatibility
          {
            path: 'line-details/:lineStudyId',
            name: 'Line Details',
            component: () =>
              import('src/components/User/LineBalancing/components/LineDetails/LineDetails.vue'),
            meta: { parent: 'Line Balancing' }
          },
          {
            path: 'work-content/:lineStudyId',
            name: 'Work Content',
            component: () =>
              import('src/components/User/LineBalancing/components/WorkContent/WorkContent.vue'),
            meta: { parent: 'Line Balancing' }
          },
          // Results Pages
          {
            path: 'results/:lineStudyId',
            name: 'Results',
            component: () =>
              import('src/components/User/LineBalancing/components/Results/ResultsPage.vue'),
            meta: { parent: 'Line Balancing' }
          },
          // Demand Planner Flow - Nested Routes
          {
            path: 'demand-flow/:lineStudyId',
            name: 'Demand Planner Flow',
            component: () =>
              import('src/components/User/LineBalancing/components/DemandPlannerFlow.vue'),
            meta: { parent: 'Line Balancing', title: 'Demand Planner' },
            children: [
              {
                path: 'demand-input',
                name: 'Demand Input',
                component: () =>
                  import(
                    'src/components/User/LineBalancing/components/DemandPlanner/DemandInput.vue'
                  ),
                meta: { parent: 'Line Balancing', title: 'Demand Input', stepIndex: 0 }
              },
              {
                path: 'time-input',
                name: 'Time Input',
                component: () =>
                  import(
                    'src/components/User/LineBalancing/components/DemandPlanner/TimeInput.vue'
                  ),
                meta: { parent: 'Line Balancing', title: 'Time Input', stepIndex: 1 }
              },
              {
                path: 'mapping',
                name: 'Mapping',
                component: () =>
                  import('src/components/User/LineBalancing/components/DemandPlanner/Mapping.vue'),
                meta: { parent: 'Line Balancing', title: 'Mapping', stepIndex: 2 }
              },
              {
                path: 'staffing',
                name: 'Staffing',
                component: () =>
                  import('src/components/User/LineBalancing/components/DemandPlanner/Staffing.vue'),
                meta: { parent: 'Line Balancing', title: 'Staffing', stepIndex: 3 }
              },
              {
                path: 'work-content-graph',
                name: 'Work Content Graph',
                component: () =>
                  import(
                    'src/components/User/LineBalancing/components/Results/WorkContentGraph.vue'
                  ),
                meta: { parent: 'Line Balancing', title: 'Work Content Graph', stepIndex: 4 }
              }
            ]
          }
        ]
      },
      // {
      //   path: 'subscription',
      //   name: 'Subscription',
      //   component: () => import('src/components/User/Subscription/Index.vue')
      // },
      {
        path: 'work-instructions',
        name: 'Work Instructions',
        meta: { moduleCode: MODULES.WORK_INSTRUCTIONS.moduleCode },
        component: () => import('src/components/User/WorkInstructions/Index.vue'),
        children: [
          // {
          //   path: 'studies',
          //   name: 'workStudies',
          //   component: () => import('src/components/User/WorkInstructions/StudyList.vue'),
          //   meta: { title: 'Work Studies', parent: 'Work Instructions' }
          // },
          {
            path: 'studies/:id(\\d+)?',
            name: 'workStudies',
            component: () => import('src/components/User/WorkInstructions/StudyListv2.vue'),
            meta: { title: 'Work Studies', parent: 'Work Instructions' }
          },
          {
            path: 'search',
            name: 'Search Work Instruction Records',
            component: () =>
              import('src/components/User/WorkInstructions/FolderManagement/Search.vue'),
            meta: { title: 'Work Studies', parent: 'Work Instructions' },
            beforeEnter: [addStateInMeta]
          },
          {
            path: 'study/:id',
            name: 'work-new-study',
            component: () => import('src/components/User/WorkInstructions/EditStudy.vue')
          },
          {
            path: 'study/:id/result',
            name: 'work-study-result',
            component: () => import('src/components/User/WorkInstructions/StudyResult.vue'),
            meta: { title: 'Work Instructions Results', parent: 'Work Instructions' }
          }
        ]
      },

      // {
      //   path: 'ergonomics',
      //   name: 'Ergonomics',
      //   component: () => import('src/components/User/Ergonomics/Index.vue'),
      //   children: [
      //     {
      //       path: 'assessments',
      //       name: 'Assessments',
      //       component: () =>
      //         import('src/components/User/Ergonomics/ErgonomicsRecords/ErgonomicsRecordsv2.vue'),
      //       meta: { title: 'Ergonomics', parent: 'Ergonomics' },
      //       children: [
      //         {
      //           path: ':id?',
      //           name: 'Assessments Folders',
      //           component: () =>
      //             import(
      //               'src/components/User/Ergonomics/ErgonomicsRecords/ErgonomicsRecordsv2.vue'
      //             ),
      //           meta: { title: 'Assessments Folders', parent: 'Assessments' }
      //         }
      //       ]
      //     },
      //     {
      //       path: 'ergonomic-assessment',
      //       name: 'Ergonomic Assessment',
      //       component: () => import('src/components/User/Ergonomics/ErgonomicAssessment/Index.vue'),
      //       meta: { parent: 'Ergonomics' }
      //     },
      //     {
      //       path: 'ergonomic-result',
      //       name: 'Ergonomic Result',
      //       component: () => import('src/components/User/Ergonomics/AssessmentResult/Index.vue'),
      //       meta: { parent: 'Ergonomics' }
      //     }
      //   ]
      // },
      {
        path: 'ergonomics',
        name: 'Ergonomics',
        meta: { moduleCode: MODULES.ERGONOMICS.moduleCode },
        component: () => import('src/components/User/Ergonomics/Index.vue'),
        children: [
          {
            path: 'assessments/:id(\\d+)?',
            name: 'Assessments',
            component: () =>
              import('src/components/User/Ergonomics/ErgonomicsRecords/ErgonomicsRecordsv2.vue'),
            meta: { title: 'Ergonomics', parent: 'Ergonomics' }
          },
          {
            path: 'ergonomic-assessment',
            name: 'Ergonomic Assessment',
            component: () => import('src/components/User/Ergonomics/ErgonomicAssessment/Index.vue'),
            meta: { parent: 'Ergonomics' }
          },
          {
            path: 'ergonomic-result',
            name: 'Ergonomic Result',
            component: () => import('src/components/User/Ergonomics/AssessmentResult/Index.vue'),
            meta: { parent: 'Ergonomics' }
          },
          {
            path: 'assessments-comparison/:id',
            name: 'Edit Ergonomic Assessments Comparison',
            component: () =>
              import(
                'src/components/User/Ergonomics/AssessmentComparison/EditAssessmentsComparison/Index.vue'
              ),
            meta: { parent: 'Ergonomics' }
          },
          {
            path: 'assessments-comparison-result/:id',
            name: 'Ergonomic Assessments Comparison Result',
            component: () =>
              import(
                'src/components/User/Ergonomics/AssessmentComparison/ResultAssessmentsComparison/Index.vue'
              ),
            meta: { parent: 'Ergonomics' }
          },
          {
            path: 'search',
            name: 'Search Ergonomics Records',
            component: () =>
              import(
                'src/components/User/Ergonomics/ErgonomicsRecords/FolderManagement/Search.vue'
              ),
            meta: { title: 'Search Result', parent: 'Ergonomics' },
            beforeEnter: [addStateInMeta]
          }
        ]
      },
      {
        path: 'fmea',
        name: 'Quality Planning',
        meta: { moduleCode: MODULES.FMEA.moduleCode },
        component: () => import('src/components/User/FMEA/Index.vue'),
        children: [
          {
            path: 'records-list',
            name: 'FMEA Records',
            component: () => import('src/components/User/FMEA/FMEARecords/Index.vue'),
            meta: { title: 'Quality Planning', parent: 'Quality Planning' }
          },
          {
            path: 'fmea-assessment/:id',
            name: 'FMEA Assessment',
            component: () => import('src/components/User/FMEA/FMEAAssessment/Index.vue'),
            meta: { title: 'Quality Planning Assessment', parent: 'Quality Planning' }
          },
          {
            path: 'fmea-result/:id',
            name: 'Result Assessment',
            component: () => import('src/components/User/FMEA/AssessmentResult/Index.vue'),
            meta: { title: 'Quality Planning Assessment', parent: 'Quality Planning' }
          }
        ]
      },
      {
        path: 'pmts',
        name: 'PMTS',
        meta: { moduleCode: MODULES.PMTS.moduleCode },
        component: () => import('src/components/User/PMTS/Index.vue'),
        children: [
          {
            path: 'studies/:id(\\d+)?',
            name: 'pmts-studies',
            component: () => import('src/components/User/PMTS/StudyList/StudyListv2.vue'),
            meta: { title: 'Predetermined Motion Time System', parent: 'PMTS' }
          },
          {
            path: 'search',
            name: 'Search PMTS Studies',
            component: () =>
              import('src/components/User/PMTS/StudyList/FolderManagement/Search.vue'),
            meta: { title: 'Predetermined Motion Time System', parent: 'PMTS' },
            beforeEnter: [addStateInMeta]
          },
          {
            path: 'study/:id',
            name: 'pmts-study-edit',
            components: {
              modal: () => import('src/components/User/PMTS/EditStudy.vue')
            },
            meta: { title: 'Predetermined Motion Time System', parent: 'PMTS' }
            // beforeEnter: [keepDefaultView]
          },
          {
            path: 'study/result/:id',
            name: 'pmts-study-result',
            component: () => import('src/components/User/PMTS/StudyResult/StudyResult.vue'),
            meta: { title: 'Predetermined Motion Time System Result', parent: 'PMTS' }
          }
        ]
      },
      {
        path: 'assembly-automation',
        name: 'Assembly Automation',
        meta: { moduleCode: MODULES.ASSEMBLY_AUTOMATION.moduleCode },
        component: () => import('src/components/User/AssemblyAutomation/Index.vue'),
        children: [
          {
            path: ':id?',
            name: 'assembly-automation-studies',
            component: () =>
              import(
                'src/components/User/AssemblyAutomation/FolderManagement/ListComponent.vue'
              ),
            meta: { title: 'Assembly Automation', parent: 'Assembly Automation' }
          },
          {
            path: 'search',
            name: 'assembly-automation-search',
            component: () =>
              import('src/components/User/AssemblyAutomation/FolderManagement/Search.vue'),
            meta: { title: 'Assembly Automation', parent: 'Assembly Automation' }
          },
          {
            path: 'study/:id',
            name: 'assembly-automation-edit',
            components: {
              modal: () => import('src/components/User/AssemblyAutomation/EditStudy.vue')
            },
            meta: { title: 'Assembly Automation', parent: 'Assembly Automation' }
          }
        ]
      }
    ]
  }
]
